// Isometric projection with an orbiting camera. The ground plane is turned
// by the view angle around the middle of the map, then projected: one tile
// is a diamond TILE_W wide and TILE_H tall when the angle is zero. Every
// screen position and every hit test goes through here, so the whole game
// follows the angle.
import { MAP_SIZE, TILE } from '@thrash/sim';

export const TILE_W = 32;
export const TILE_H = 16;

const C = MAP_SIZE / 2;
let angle = 0, cos = 1, sin = 0;

/** The view angle in radians, 0 being the original view. */
export function viewAngle(): number {
  return angle;
}

/** Sets the view angle; callers redraw whatever they keep cached. */
export function setViewAngle(a: number): void {
  const full = Math.PI * 2;
  angle = ((a % full) + full) % full;
  cos = Math.cos(angle);
  sin = Math.sin(angle);
}

/** Tile-space (fractional tiles) to screen. */
export function toScreen(tx: number, ty: number): [number, number] {
  const dx = tx - C, dy = ty - C;
  const rx = dx * cos - dy * sin + C, ry = dx * sin + dy * cos + C;
  return [(rx - ry) * (TILE_W / 2), (rx + ry) * (TILE_H / 2)];
}

/** Screen (world-local pixels) to fractional tile-space. */
export function toTile(sx: number, sy: number): [number, number] {
  const a = sx / (TILE_W / 2);
  const b = sy / (TILE_H / 2);
  const dx = (a + b) / 2 - C, dy = (b - a) / 2 - C;
  return [dx * cos + dy * sin + C, -dx * sin + dy * cos + C];
}

/** How far into the screen a tile-space point is: larger is nearer the viewer, so it draws later. */
export function depth(tx: number, ty: number): number {
  const dx = tx - C, dy = ty - C;
  return (dx * cos - dy * sin) + (dx * sin + dy * cos);
}

/** Sim units to screen. */
export function unitsToScreen(x: number, y: number): [number, number] {
  return toScreen(x / TILE, y / TILE);
}
